import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { format } from 'date-fns';
import { Auth } from 'aws-amplify';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import {
  updateApplicationQcStatus,
  updateApplications,
  deleteApplication
} from '../../api/FDALetters';
import Actions from '../../store/actions';
import Store from '../../store';

import PDFViewer from '../PDFViewer';

const ApplicationModal: React.FC<any> = ({ closeEdit, letterData }) => {
  const { dispatch } = useContext<any>(Store);

  const [applications, setApplications] = useState(letterData.applications);
  const [status, setStatus] = useState(letterData.application_qc_status || 'to_qc');
  const [applicationToDelete, setApplicationToDelete] = useState<any>();

  const untitledPDF = letterData?.pdfs?.filter((p: any) => p.document_type === 'Untitled Letter');
  const [pdf, setPDF] = useState(untitledPDF[0] || letterData?.pdfs[0]);

  const addNewApplication = () => {
    const newApplication = {
      application_number: 'new',
      application_type: 'type',
      brand_name: 'NA',
      center: letterData.source_page.includes('CDER') ? 'CDER' : 'CBER',
      letter_id: letterData.fda_letter_id
    };
    const newApplicationList = [...applications, newApplication];
    setApplications(newApplicationList);
  };
  const handleInputChange = (index: number, key: string, value: string) => {
    const updatedApplication = {
      ...applications[index],
      [key]: value
    };
    const newApplicationList = [...applications];
    newApplicationList[index] = updatedApplication;

    setApplications(newApplicationList);
  };

  const saveApplicationEdits = async () => {
    try {
      const user = await Auth.currentUserInfo();
      const response = await updateApplications(letterData.fda_letter_id, applications);
      const qcStatusResponse = await updateApplicationQcStatus(letterData.fda_letter_id, {
        application_qc_status: status,
        curated_by: user.attributes.email
      });
      if (response.data.status !== 200 || qcStatusResponse.data.status !== 200) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Application Data Update failed' }
        });
        return;
      }
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Applications updated Successfully', color: 'success' }
      });
    } catch (err) {
      console.error(err);
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Applications Update failed' }
      });
    }
  };

  const removeApplication = async (index: number, application: any) => {
    if (application.id) {
      try {
        const response = await deleteApplication(application.letter_id, application.id);
        console.log(response);
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Page Data Deleted Successfully', color: 'success' }
        });

        const updatedApplications = [...applications];
        updatedApplications.splice(index, 1);

        setApplications(updatedApplications);
      } catch (err) {
        console.error(err);
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Page Data Delete failed' }
        });
      }
    } else {
      const updatedApplications = [...applications];
      updatedApplications.splice(index, 1);

      setApplications(updatedApplications);
    }
    setApplicationToDelete(null);
  };

  return (
    <Box p={2} position='relative'>
      <Stack direction='row' alignItems='center' justifyContent='center' spacing={2}>
        <Typography fontWeight={700}>
          {format(new Date(letterData.issue_date), 'yyyy-MM-dd')}
        </Typography>
        <Typography fontWeight={600}>{letterData.subject}</Typography>
      </Stack>
      <IconButton
        aria-label='close'
        onClick={closeEdit}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          zIndex: 100
        }}>
        <CloseIcon />
      </IconButton>
      <Grid container>
        <Grid item xl={4} minWidth={6} height='80vh' overflow='hidden'>
          <Box height='90%' overflow='auto' p={1} py={2}>
            {applications.map((application: any, index: number) => {
              return (
                <Box key={application.application_number}>
                  <Stack direction='row' spacing={0} py={1}>
                    <TextField
                      label='Type'
                      value={application.application_type || ''}
                      onChange={e => handleInputChange(index, 'application_type', e.target.value)}
                      variant='outlined'
                      size='small'
                      sx={{ width: 100 }}
                    />
                    <TextField
                      label='Number'
                      value={application.application_number || ''}
                      onChange={e => handleInputChange(index, 'application_number', e.target.value)}
                      variant='outlined'
                      size='small'
                      sx={{ width: 150 }}
                    />
                    <TextField
                      label='Brand Name'
                      value={application.brand_name || ''}
                      onChange={e => handleInputChange(index, 'brand_name', e.target.value)}
                      variant='outlined'
                      size='small'
                    />
                    <IconButton onClick={() => setApplicationToDelete({ index, application })}>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Box>

          <Stack direction='row' justifyContent='space-between'>
            <Button variant='outlined' onClick={addNewApplication}>
              Add Application
            </Button>
            <Box>
              <TextField
                variant='outlined'
                id='status'
                label='QC Status'
                select
                value={status}
                onChange={e => setStatus(e.target.value)}
                InputProps={{
                  style: { fontSize: 12, minWidth: '100px', height: 36 }
                }}
                InputLabelProps={{
                  style: { fontSize: 12 }
                }}>
                <MenuItem value='qced'>Qced</MenuItem>
                <MenuItem value='to_qc'>To QC</MenuItem>
                <MenuItem value='escalate'>Escalate</MenuItem>
                <MenuItem value='missing details'>Missing Details</MenuItem>
                <MenuItem value='not applicable'>Not Applicable</MenuItem>
              </TextField>
              <Button variant='contained' onClick={saveApplicationEdits}>
                Save
              </Button>
            </Box>
          </Stack>
        </Grid>
        <Grid item xl={6} height='80vh' overflow='hidden'>
          <Stack direction='row' spacing={1} justifyContent='center'>
            {letterData.pdfs.length > 0
              ? letterData.pdfs.map((p: any) => (
                  <Button
                    key={p.doc_id}
                    onClick={() => setPDF(p)}
                    sx={{
                      bgcolor: p.doc_id === pdf.doc_id ? 'primary.backgroundLight' : 'none'
                    }}>
                    {p.document_type}
                  </Button>
                ))
              : 'No PDFs found'}
          </Stack>
          <Box height='90%' px={3}>
            <Box
              flex={1}
              sx={{
                height: '100%',
                overflow: 'auto',
                position: 'relative'
              }}>
              {pdf ? (
                <PDFViewer fileUrl={pdf.signed_url} />
              ) : (
                <Typography fontSize={24} fontWeight='bold' mt={12}>
                  No Documents available
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      {!!applicationToDelete && (
        <Dialog open={!!applicationToDelete} onClose={() => setApplicationToDelete(null)}>
          <DialogTitle>Delete Record</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete {applicationToDelete.application.application_number}{' '}
              record? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApplicationToDelete(null)}>Cancel</Button>
            <Button
              onClick={() =>
                removeApplication(applicationToDelete.index, applicationToDelete.application)
              }
              color='error'
              autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default React.memo(ApplicationModal);
